import { RefreshOutlined } from '@mui/icons-material';
import { Autocomplete, Box, Button, Collapse, IconButton, TextField } from '@mui/material';
import {
  GridFilterModel,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  useGridApiContext,
} from '@mui/x-data-grid';

export interface FilterModel {
  columnName: { field: string; headerName: string };
  options: { buttonName: string; value: string }[];
  operator: 'equals' | 'contains' | 'startsWith' | 'endsWith';
  // bypass the grid filter model (community DataGrid allows only one item) — filters stack server-side
  onSelect?: (value: string | null) => void;
}

interface Props {
  refetch: () => void;
  isFetching: boolean;
  filters?: FilterModel[];
  extra?: React.ReactNode;
}

export function TableToolbar({ refetch, isFetching, filters, extra }: Props) {
  const apiRef = useGridApiContext();
  const handleButtonClick = (value: string, field: string) => {
    const clicked = filters?.find((fm) => fm.columnName.field === field);
    if (clicked?.onSelect) {
      clicked.onSelect(value || null);
      return;
    }
    if (!value) {
      apiRef.current.setFilterModel({ items: [] });
      return;
    }
    if (!clicked) return;
    const newFilterModel: GridFilterModel = {
      items: [{ field, value, operator: clicked.operator }],
    };
    apiRef.current.setFilterModel(newFilterModel);
  };

  return (
    <GridToolbarContainer sx={{ alignItems: 'center', justifyContent: 'flex-start', padding: 0.5 }}>
      <GridToolbarDensitySelector />
      <GridToolbarFilterButton />
      <Box sx={{ flex: 1 }}>
        {filters?.map((filter) => (
          <Autocomplete
            key={filter.columnName.field}
            sx={{ m: 1, minWidth: 220, display: 'inline-flex' }}
            options={filter.options}
            getOptionLabel={(option) => option.buttonName}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            onChange={(_, option) => handleButtonClick(option?.value ?? '', filter.columnName.field)}
            ListboxProps={{ style: { maxHeight: 300 } }}
            renderInput={(params) => <TextField {...params} label={filter.columnName.headerName} />}
          />
        ))}
      </Box>
      {extra}
      <Box>
        {refetch && (
          <IconButton onClick={() => refetch()}>
            <RefreshOutlined
              sx={
                isFetching
                  ? {
                      color: '#1D2434',
                      animation: 'spin 2s linear infinite',
                      '@keyframes spin': {
                        '100%': {
                          transform: 'rotate(360deg)',
                        },
                        '0%': {
                          transform: 'rotate(0deg)',
                        },
                      },
                    }
                  : { color: '#67C5B0' }
              }
            />
          </IconButton>
        )}
      </Box>
    </GridToolbarContainer>
  );
}

export default TableToolbar;
